import { useCallback, useEffect, useMemo } from 'react';
import { useDebounceState } from '@common-fe/common-fe';

import { CoverageLevelPayload } from '../CoverageLevels.constants';
import { splitTriggerAmountsFromValues } from '../CoverageLevels.utils';
import { updateItemByCoverageId, useStore } from '../stores/useCoverageLevels.store';

import useCoverageLevelsFields from './useCoverageLevelsFields';
import { useDeleteCoverageLevelById } from './';

interface UseCoverageLevelsItemProps {
  coverageId: string;
  isEditMode?: boolean;
  isTemplateMode?: boolean;
  isActiveMode?: boolean;
}

const useCoverageLevelsItem = ({
  coverageId,
  isEditMode,
  isTemplateMode,
  isActiveMode,
}: UseCoverageLevelsItemProps) => {
  const handleDelete = useDeleteCoverageLevelById(coverageId);

  const fields = useCoverageLevelsFields(coverageId, {
    isEditMode,
    isTemplateMode,
    isActiveMode,
  });

  const showErrors = useStore((state) => state.showErrors);

  const accountFundingConfigs = useStore(
    useCallback(
      (state) => state.state?.find((item) => item?.coverageId === coverageId)?.accountFundingConfigs,
      [coverageId]
    )
  );

  const categorySpendLimits = useStore(
    useCallback(
      (state) => state.state?.find((item) => item?.coverageId === coverageId)?.categorySpendLimits,
      [coverageId]
    )
  );

  const formKey = useMemo(() => {
    let key = coverageId;

    if (accountFundingConfigs) {
      const planFlags = accountFundingConfigs
        .filter((config) => config.canSetHealthPlanSpendLimits === false)
        .map((config) => config.planId)
        .join('|');

      if (planFlags) key = `${key}-noSpendLimits:${planFlags}`;
    }

    if (categorySpendLimits) {
      const catKey = categorySpendLimits.map((limit) => `${limit.categoryType}`).join('|');

      if (catKey) key = `${key}-cat:${catKey}`;
    }

    return key;
  }, [coverageId, accountFundingConfigs, categorySpendLimits]);

  const { setValues, values } = useDebounceState<Partial<CoverageLevelPayload>>();

  useEffect(() => {
    if (values) {
      updateItemByCoverageId(coverageId, splitTriggerAmountsFromValues(values as Record<string, unknown>));
    }
  }, [coverageId, values]);

  return {
    handleDelete,
    fields,
    showErrors,
    formKey,
    setValues,
  };
};

export default useCoverageLevelsItem;
